import { useState } from 'react';
import { api, type ExternalAllocPlan, type ExternalPick } from '../lib/api';
import { ErrorBox } from './DataTable';

/**
 * 외부 편성 한 번에 등록(2026-10-08 way 「관광사 이름이나 개인 이름만 적고 일괄 양식으로」).
 *
 * 차주·기사·차량 이름을 직원이 따로 쓰지 않는다 — 이름 하나만 받고 서버가 「운행편성 표기방식」대로 만든다
 * (차주 = 이름 그대로 · 기사·차량 = `개인_이름` / `관광사_이름`). 같은 사업자번호·계좌·휴대전화의 차주가 있으면 **그 차주를 다시 쓴다**.
 * 「확인」 = 미리보기(아무것도 안 씀) → 무엇을 다시 쓰고 무엇을 새로 만드는지 보여 준 뒤 「등록」.
 */
type Kind = '관광사' | '개인';

export default function ExternalAllocForm({ onClose, onSaved }: { onClose: () => void; onSaved: (msg: string, carAllocId?: number) => void }) {
  const [kind, setKind] = useState<Kind>('관광사');
  const [f, setF] = useState<Record<string, string>>({ fullCnt: '45' });
  const [plan, setPlan] = useState<ExternalAllocPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: string, v: string) => { setF((cur) => ({ ...cur, [k]: v })); setPlan(null); };
  const name = (f.name ?? '').trim();
  const shown = name ? `${kind}_${name}` : `${kind}_이름`;

  const payload = (commit: boolean) => ({
    kind, name: f.name, mobilePhone: f.mobilePhone, phone: f.phone,
    bizNumber: kind === '관광사' ? f.bizNumber : undefined, email: kind === '관광사' ? f.email : undefined,
    bankName: kind === '개인' ? f.bankName : undefined, bank: kind === '개인' ? f.bank : undefined,
    resNo: kind === '개인' ? f.resNo : undefined,
    comment: f.comment, startdt: f.startdt, enddt: f.enddt, fullCnt: f.fullCnt, commit,
  });

  async function run(commit: boolean) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.registerExternalAlloc(payload(commit));
      if (commit) onSaved(`외부 편성을 등록했습니다 — NO ${r.carAllocId} · ${r.car.name}`, r.carAllocId);
      else setPlan(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const field = (k: string, label: string, ph?: string, req = false, type = 'text') => (
    <label className="block text-sm">
      <span className="text-ink-soft">{label}{req && <b className="text-red-600"> *</b>}</span>
      <input className="field mt-1 w-full" type={type} placeholder={ph} value={f[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
    </label>
  );

  const row = (label: string, p: ExternalPick) => (
    <tr key={label} className="border-t border-line">
      <td className="py-1.5 pr-3 text-ink-soft">{label}</td>
      <td className="py-1.5 pr-3 font-medium">{p.name}</td>
      <td className="py-1.5">
        {p.reuse
          ? <span className="rounded bg-sky-50 px-2 py-0.5 text-xs text-sky-800">있는 것 다시 씀 #{p.id}</span>
          : <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">새로 만듦</span>}
        {p.fixPrivate && <span className="ml-2 text-xs text-amber-700">차주 구분을 「{kind}」로 고칩니다</span>}
      </td>
    </tr>
  );

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">외부 편성 등록</h2>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={busy}>취소</button>
        {plan && !plan.existing && !plan.needRrn
          ? <button className="btn-primary" onClick={() => void run(true)} disabled={busy}>{busy ? '등록 중…' : '등록'}</button>
          : <button className="btn-primary" onClick={() => void run(false)} disabled={busy}>{busy ? '확인 중…' : '확인'}</button>}
      </div>
      <p className="text-sm text-ink-soft">
        이름만 적으면 차주·기사·차량·편성이 양식대로 한 번에 만들어집니다.
        같은 {kind === '관광사' ? '사업자번호' : '계좌'}의 차주(또는 같은 휴대전화·같은 이름의 차주)가 이미 있으면 새로 만들지 않고 그 차주를 씁니다.
      </p>
      {error && <ErrorBox message={error} />}

      <div className="flex gap-2">
        {(['관광사', '개인'] as Kind[]).map((k) => (
          <button key={k} type="button" onClick={() => { setKind(k); setPlan(null); }}
                  className={`rounded-lg px-4 py-1.5 text-sm font-medium ${kind === k ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
            {k === '관광사' ? '관광사(세금계산서 청구)' : '개인(3.3% 지급)'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {field('name', kind === '관광사' ? '관광사 이름' : '개인 이름', kind === '관광사' ? '예: 제로쿨투어 — 「관광사_」는 붙이지 않음' : '예: 홍길동 — 「개인_」은 붙이지 않음', true)}
        {field('mobilePhone', '휴대전화', '010-0000-0000', true)}
        {field('phone', '전화', '031-000-0000 (사무실)')}
        {kind === '관광사' ? (
          <>
            {field('bizNumber', '사업자번호', '000-00-00000', true)}
            {field('email', '이메일', '세금계산서 받을 이메일', true)}
          </>
        ) : (
          <>
            {field('bankName', '은행', '국민 · 신한 · 농협 …', true)}
            {field('bank', '계좌', '000-000-000000', true)}
            {/* 3.3% 원천징수 — 기사 주민번호(암호화 저장 · 화면엔 가린 값만 · 10-29 way). 다시 쓰는 기사에 이미 있으면 비워 둬도 된다. */}
            <label className="block text-sm">
              <span className="text-ink-soft">기사 주민번호{!plan?.hasRrn && <b className="text-red-600"> *</b>}</span>
              <input className="field mt-1 w-full" autoComplete="off" inputMode="numeric" placeholder={plan?.hasRrn ? '이미 등록됨 — 바꿀 때만' : '900101-1234567'}
                     value={f.resNo ?? ''} onChange={(e) => set('resNo', e.target.value.replace(/[^0-9-]/g, ''))} />
            </label>
          </>
        )}
        {field('fullCnt', '정원', '45', false, 'number')}
        {field('startdt', '시작일', '비우면 기간 없이', false, 'date')}
        {field('enddt', '종료일', '비우면 무기한', false, 'date')}
        <label className="block text-sm md:col-span-3">
          <span className="text-ink-soft">정산 메모</span>
          <textarea className="field mt-1 w-full" rows={2} placeholder={kind === '관광사' ? '예: 세금계산서 발행' : '예: 3.3% 원천징수'}
                    value={f.comment ?? ''} onChange={(e) => set('comment', e.target.value)} />
        </label>
      </div>

      <div className="rounded-lg border border-line bg-canvas px-3 py-2 text-sm">
        만들어질 이름 — 차주 <b>{name || '이름'}</b> · 기사 <b>{shown}</b> · 차량 <b>{shown}</b> · 소속 <b>외부</b>
      </div>

      {plan && (
        <div className="space-y-2">
          {plan.existing && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              같은 외부 편성이 이미 있습니다 — <b>NO {plan.existing.carAllocId}</b>{plan.existing.enddt ? ` (종료 ${plan.existing.enddt})` : ''}.
              새로 만들지 말고 그 편성을 쓰세요.
            </div>
          )}
          <table className="w-full text-sm">
            <tbody>
              {row('차주', plan.owner)}
              {row('기사', plan.driver)}
              {row('차량', plan.car)}
            </tbody>
          </table>
          {plan.sharedPhone && <p className="text-xs text-amber-700">이 휴대전화는 여러 분이 같이 쓰는 번호입니다 — 그대로 등록되지만, 기사앱 인증번호 로그인은 이 번호로는 되지 않습니다.</p>}
          {plan.needRrn && <p className="text-sm text-red-700">외부 개인(3.3% 지급)은 기사 주민번호가 필요합니다 — 넣고 다시 「확인」하세요.</p>}
          {!plan.existing && !plan.needRrn && <p className="text-xs text-ink-soft">맞으면 「등록」을 누르세요. 입력을 바꾸면 다시 「확인」합니다.</p>}
        </div>
      )}
    </div>
  );
}
